import React, { useCallback, useEffect, useState } from 'react';
import { debounce, isEmpty, throttle } from 'lodash';
import { useTranslate } from 'react-admin';
import { useQuery } from 'react-query';

import { Grid } from '@material-ui/core';

import { SelectInput, TextInput } from '~/components';

import {
  createConsent,
  CreateConsentRequest,
  getSubtypes,
  updateConsent,
} from '~/api/serviceCharge';
import { Account } from '~/types';
import { HeadTransaction } from '~/types/HeadTransaction';
import { ConsentHolder, RunTransferActionFormProps } from './RunTransferActionTypes';

import { l } from './RunTransferAction';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';

type ServiceChargeFormProps = RunTransferActionFormProps;

type ServiceChargeSession = {
  isSending: boolean;
  nextRequest?: CreateConsentRequest;
  lastConsent?: HeadTransaction;
};

const SERVICE_CHARGE_SESSION: ServiceChargeSession = {
  isSending: false,
};

const NO_CONSENT: ConsentHolder = {};

const ServiceChargeForm = ({ resource, profileId, onConsent }: ServiceChargeFormProps) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const [senderAccount, setSenderAccount] = useState<Account | undefined>();
  const [operationSubtype, setOperationSubtype] = useState<string | undefined>();
  const [referenceId, setReferenceId] = useState<string | undefined>();
  const [note, setNote] = useState<string | undefined>();

  const debouncedSetNote = useCallback(debounce(setNote, 500), []);
  const debouncedSetReferenceId = useCallback(debounce(setReferenceId, 500), []);

  const senderAccountId = senderAccount?.id;

  const { data: subtypesResponse, isFetching: subtypesLoading } = useQuery(
    ['serviceChargeSubtypes', senderAccountId],
    () => getSubtypes(senderAccountId!),
    { enabled: !!senderAccountId }
  );

  const subtypes = subtypesResponse?.data?.subtypes || [];
  const subtypeOptions = isEmpty(subtypes)
    ? [{ value: undefined, text: translate('utils.noOptions') }]
    : subtypes.map(({ id, name }) => ({ value: id, text: name }));

  useEffect(() => {
    SERVICE_CHARGE_SESSION.isSending = false;
    SERVICE_CHARGE_SESSION.nextRequest = undefined;
    SERVICE_CHARGE_SESSION.lastConsent = undefined;
  }, []);

  const throttledMakeConsent = useCallback(
    throttle(
      (
        senderAccountId: string,
        operationSubtype: string,
        note?: string,
        referenceId?: string
      ): any => {
        const request: CreateConsentRequest = {
          senderAccountId,
          operationSubtype,
          note,
          referenceId: referenceId
            ? [{ id: 'DASHBOARD_REFERENCE', value: referenceId }]
            : undefined,
        };

        const sendNextRequest = (request: CreateConsentRequest) => {
          if (SERVICE_CHARGE_SESSION.isSending) {
            SERVICE_CHARGE_SESSION.nextRequest = request;
            return;
          }

          SERVICE_CHARGE_SESSION.isSending = true;
          SERVICE_CHARGE_SESSION.nextRequest = undefined;

          const lastConsent = SERVICE_CHARGE_SESSION.lastConsent;
          const promise = lastConsent
            ? updateConsent(lastConsent.id, request)
            : createConsent(request);

          const sendPending = () => {
            SERVICE_CHARGE_SESSION.isSending = false;
            const nextRequest = SERVICE_CHARGE_SESSION.nextRequest;
            if (nextRequest) {
              SERVICE_CHARGE_SESSION.nextRequest = undefined;
              sendNextRequest(nextRequest);
            }
          };

          promise
            .then((response) => {
              SERVICE_CHARGE_SESSION.lastConsent = response.data;
              sendPending();
              onConsent({ consent: response.data });
            })
            .catch((e) => {
              sendPending();
              onConsent({ error: e?.body?.code });
            });
        };

        sendNextRequest(request);
      },
      1000,
      { leading: true }
    ),
    []
  );

  useEffect(() => {
    if (senderAccountId && operationSubtype && note) {
      onConsent(NO_CONSENT);
      throttledMakeConsent(senderAccountId, operationSubtype, note, referenceId);
    } else {
      onConsent(undefined);
    }
  }, [onConsent, throttledMakeConsent, senderAccountId, operationSubtype, note, referenceId]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <AccountTableSingleSelect
          resource={resource}
          profileId={profileId}
          label={t('debitAccountNumber')}
          value={senderAccount?.number}
          onChange={(account) => {
            setSenderAccount(account);
            setOperationSubtype(undefined);
          }}
          additionalFilters={{ type: 'REGULAR', status: 'ACTIVE' }}
          required
          chipVariant='balance'
          style={{ width: 240, marginBottom: 18 }}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectInput<string | undefined>
          options={subtypeOptions}
          label={t('operationSubtype')}
          value={operationSubtype}
          onChange={(e) => {
            setOperationSubtype(e.target.value);
            if (e.key === 'Enter') {
              e.stopPropagation();
            }
          }}
          style={{ width: 240, marginBottom: 18 }}
          disabled={!senderAccountId || subtypesLoading}
          required
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('comment')}
          style={{ width: 490 }}
          required
          onChange={(e: any) => {
            debouncedSetNote(e.target.value || undefined);
          }}
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('reference')}
          style={{ width: 282 }}
          onChange={(e: any) => {
            debouncedSetReferenceId(e.target.value || undefined);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default ServiceChargeForm;
